import React, { useEffect, useRef } from 'react';
import './BannerAd.css';

/**
 * BannerAd Component
 * Displays banner ads using Google AdSense or fallback ads
 * 
 * Props:
 * - position: 'top', 'bottom', 'sidebar' (default: 'top')
 * - adSlot: Google AdSense slot ID (required if using AdSense)
 * - size: 'responsive', '728x90', '300x250', '320x50' (default: 'responsive')
 */

const JUICYADS_SCRIPT_ID = 'juicyads-jads-script';
const JUICYADS_SCRIPT_SRC = 'https://poweredby.jads.co/js/jads.js';

function loadJuicyAdsScript() {
  if (document.getElementById(JUICYADS_SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = JUICYADS_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.setAttribute('data-cfasync', 'false');
  script.src = JUICYADS_SCRIPT_SRC;
  document.body.appendChild(script);
}

function BannerAd({ position = 'top', adSlot, size = 'responsive', network = 'adsense', juicyAdsZoneId }) {
  const adClientId = process.env.REACT_APP_AD_CLIENT_ID;
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const isJuicyAds = network === 'juicyads';
    if (!container) return undefined;
    if (isJuicyAds && !juicyAdsZoneId) return undefined;
    if (!isJuicyAds && (network !== 'adsense' || !adSlot || !adClientId)) return undefined;

    let initialized = false;
    let frameId = null;
    const pushWhenSized = () => {
      if (initialized || frameId !== null || container.getBoundingClientRect().width <= 0) return;

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        if (initialized || !container.isConnected || container.getBoundingClientRect().width <= 0) return;

        initialized = true;
        if (isJuicyAds) {
          window.adsbyjuicy = window.adsbyjuicy || [];
          window.adsbyjuicy.push({ adzone: Number(juicyAdsZoneId) });
          loadJuicyAdsScript();
          return;
        }

        try {
          window.adsbygoogle = window.adsbygoogle || [];
          window.adsbygoogle.push({});
        } catch (error) {
          console.warn('AdSense ad could not be initialized', error);
        }
      });
    };

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pushWhenSized);
    observer?.observe(container);
    pushWhenSized();

    return () => {
      observer?.disconnect();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [adClientId, adSlot, network, juicyAdsZoneId]);

  // Determine dimensions based on size prop
  const getAdDimensions = () => {
    switch(size) {
      case '728x90':
        return { width: '728px', height: '90px' };
      case '300x250':
        return { width: '300px', height: '250px' };
      case '320x50':
        return { width: '320px', height: '50px' };
      default:
        return { width: '100%', height: 'auto' };
    }
  };

  const adDimensions = getAdDimensions();
  const isResponsive = size === 'responsive';
  const hasAdConfiguration = network === 'juicyads'
    ? Boolean(juicyAdsZoneId)
    : network === 'adsense' && Boolean(adSlot && adClientId);

  return (
    <div ref={containerRef} className={`banner-ad banner-ad-${position} banner-ad-${size}`}>
      {hasAdConfiguration ? (
        network === 'juicyads' ? (
          <ins
            id={juicyAdsZoneId}
            data-width={parseInt(adDimensions.width, 10) || 728}
            data-height={parseInt(adDimensions.height, 10) || 90}
          ></ins>
        ) : (
          <ins
            className="adsbygoogle"
            style={isResponsive ? { display: 'block' } : { display: 'inline-block', ...adDimensions }}
            data-ad-client={adClientId}
            data-ad-slot={adSlot}
            data-ad-format={isResponsive ? 'auto' : size === '300x250' ? 'rectangle' : 'horizontal'}
            data-full-width-responsive="true"
          ></ins>
        )
      ) : (
        <>
          {/* Fallback Placeholder (no ad slot configured) */}
          <div className="ad-placeholder">
            <div className="ad-placeholder-content">
              <small>Advertisement Space Available</small>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default BannerAd;
